import { useLocation, useNavigate, useParams } from 'react-router';
import { useCallback, useMemo, useState } from 'react';
import useClientBankAccountQuery from 'hooks/webapi/client/useClientBankAccountNumber';
import {
  useApplicationQuery,
  useClientQuery,
  usePSD2OfflineBanksQuery,
  usePSD2ProxyBanksQuery,
  usePSD2SkipMutation,
} from 'hooks/webapi';
import useError from 'hooks/useError';
import {
  INCOME_PROOF,
  MANUAL_IDENTIFICATION,
  MANUAL_PSD2_GUIDE,
  OFFLINE_PSD2_BANKS,
  OFFLINE_PSD2_GUIDE,
  PSD2_GUIDE,
  WAITING,
} from 'Router/routes';
import { useStore } from 'hooks/useStore';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import SelectEvent from 'hooks/context/EventCollector/events/SelectEvent';

export default function usePSD2BankList() {
  const { track } = useEventCollector();
  const { showError } = useError();
  const location = useLocation();
  const params = useParams();

  const isOffline = location.pathname.includes(OFFLINE_PSD2_BANKS);

  const navigate = useNavigate();
  const { data: bankAccountNumber, isLoading: bankAccountNumberIsLoading } =
    useClientBankAccountQuery(!isOffline);
  const { data: banksList, isLoading: banksListIsLoading } =
    usePSD2ProxyBanksQuery(!isOffline);
  const { data: offlineBanksList, isLoading: offlineBanksListIsLoading } =
    usePSD2OfflineBanksQuery(isOffline);
  const { mutateAsync: skipPSD2, isPending: skipPSD2IsPending } =
    usePSD2SkipMutation();
  const { data: application } = useApplicationQuery(!isOffline);
  const { data: client } = useClientQuery(!isOffline);

  const [storeManualIdentification] = useStore(
    (store) => store.manualIdentification,
  );

  const isAskForDocuments =
    application?.resolution === 'MANUAL' &&
    application?.resolutionMessage === 'ASK_FOR_DOCUMENTS';

  const [search, setSearch] = useState('');
  const handleSearchChange = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(target.value);
    },
    [],
  );

  // Create a memoized list of banks based on the search term
  const banks = useMemo(() => {
    // Convert the search term to lowercase once instead of doing it multiple times in the filter
    const normalizedSearch = search.toLowerCase();

    // If there is a search term, filter the banks that match the search term in either the name or code
    if (search.length) {
      if (isOffline) {
        return (
          offlineBanksList?.banks.filter(
            ({ name, code }) =>
              name.toLowerCase().includes(normalizedSearch) ||
              code.toLowerCase().includes(normalizedSearch),
          ) ?? []
        );
      }
      return (
        banksList?.banks.filter(
          ({ name, code }) =>
            name.toLowerCase().includes(normalizedSearch) ||
            code.toLowerCase().includes(normalizedSearch),
        ) ?? []
      );
    }

    // Return the full list of banks if there is no search term
    return isOffline ? offlineBanksList?.banks ?? [] : banksList?.banks ?? [];
  }, [search, banksList, offlineBanksList, isOffline]); // Dependencies: Recompute when search or banksList changes

  const handleSelectBank = useCallback(
    (bankId: string, bankName: string) => {
      try {
        if (!bankId) return;

        track(
          new SelectEvent({
            description: 'PSD2BankSelection',
            value: bankName,
          }),
        );

        const queryParams = new URLSearchParams({
          bankId,
          bankName,
        });
        if (storeManualIdentification && !isOffline) {
          navigate(`${MANUAL_PSD2_GUIDE}?${queryParams.toString()}`);
        } else {
          navigate(
            `${
              isOffline ? `${OFFLINE_PSD2_GUIDE}/${params.token}` : PSD2_GUIDE
            }?${queryParams.toString()}`,
          );
        }
      } catch (e) {
        if (e instanceof Error) {
          showError(e);
        }
      }
    },
    [storeManualIdentification, isOffline],
  );

  const handleSkip = useCallback(async () => {
    await skipPSD2();
    if (storeManualIdentification) {
      navigate(MANUAL_IDENTIFICATION);
    } else if (isAskForDocuments) {
      navigate(INCOME_PROOF);
    } else {
      navigate(WAITING);
    }
  }, []);

  const navigateToManual = useCallback(() => {
    const isClientIdentifiedByBankID =
      client?.identified && client?.identifiedBy === 'BANK_ID';

    navigate(isClientIdentifiedByBankID ? INCOME_PROOF : MANUAL_IDENTIFICATION);
  }, [client?.identified, client?.identifiedBy, navigate]);

  const isVisibleNotInList = useMemo((): boolean => {
    return !isOffline && client?.status !== 'VALIDATED';
  }, [client?.status, isOffline]);

  const isBanksListLoading = isOffline
    ? offlineBanksListIsLoading
    : banksListIsLoading;

  return {
    search,
    handleSearchChange,
    clientHasBankAccount: !!bankAccountNumber?.bankAccountNumber,
    clientHasAccountIsLoading: bankAccountNumberIsLoading,
    banks,
    skipPSD2IsPending,
    navigateToManual,
    handleSelectBank,
    handleSkip,
    isOffline,
    isVisibleNotInList,
    isBanksListLoading,
  };
}
